import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import {
  listRoles,
  getAccountRoles,
  getAccountsByRole,
  assignRole,
  unassignRole,
} from '../../api/rbac';
import DWLFLoader from '../../components/DWLFLoader';

const UserRoleAssignment = () => {
  const currentUser = useSelector((state) => state.account.user);

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [accountsWithRole, setAccountsWithRole] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [error, setError] = useState('');
  const [accountError, setAccountError] = useState('');

  // Search for user by accountId
  const [searchAccountId, setSearchAccountId] = useState('');
  const [searchedAccountRoles, setSearchedAccountRoles] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Assign role dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignAccountId, setAssignAccountId] = useState('');
  const [assignRoleId, setAssignRoleId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');

  // Unassign confirmation
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState(null);
  const [unassigning, setUnassigning] = useState(false);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listRoles();
      setRoles(data?.roles || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const loadAccountsForRole = useCallback(async (roleId) => {
    if (!roleId) {
      setAccountsWithRole([]);
      return;
    }

    setLoadingAccounts(true);
    setAccountError('');
    try {
      const data = await getAccountsByRole(roleId);
      setAccountsWithRole(data?.accounts || []);
    } catch (err) {
      setAccountError(err.response?.data?.message || 'Failed to load accounts');
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRole) {
      loadAccountsForRole(selectedRole.roleId);
    }
  }, [selectedRole, loadAccountsForRole]);

  const handleSearchAccount = async () => {
    if (!searchAccountId.trim()) {
      setSearchError('Please enter an account ID');
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    setSearchedAccountRoles(null);

    try {
      const data = await getAccountRoles(searchAccountId.trim());
      setSearchedAccountRoles(data);
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Failed to load account roles');
    } finally {
      setSearchLoading(false);
    }
  };

  const openAssignDialog = (accountId = '') => {
    setAssignAccountId(accountId);
    setAssignRoleId('');
    setAssignError('');
    setAssignDialogOpen(true);
  };

  const closeAssignDialog = () => {
    setAssignDialogOpen(false);
    setAssignAccountId('');
    setAssignRoleId('');
    setAssignError('');
  };

  const handleAssign = async () => {
    if (!assignAccountId.trim()) {
      setAssignError('Account ID is required');
      return;
    }
    if (!assignRoleId) {
      setAssignError('Please select a role');
      return;
    }

    setAssigning(true);
    setAssignError('');

    try {
      await assignRole(assignAccountId.trim(), assignRoleId);
      closeAssignDialog();

      // Refresh relevant data
      if (selectedRole?.roleId === assignRoleId) {
        loadAccountsForRole(assignRoleId);
      }
      // Refresh searched account roles if we assigned to the same account
      // Use assignAccountId directly (not searchAccountId) to avoid stale data
      if (searchedAccountRoles?.accountId === assignAccountId.trim()) {
        const data = await getAccountRoles(assignAccountId.trim());
        setSearchedAccountRoles(data);
      }
    } catch (err) {
      setAssignError(err.response?.data?.message || 'Failed to assign role');
    } finally {
      setAssigning(false);
    }
  };

  // Check if a role grants admin access via permissions (not just name)
  const roleGrantsAdminAccess = (permissions) => {
    return permissions?.some(p => p === '*' || p === 'admin:*');
  };

  const openUnassignDialog = (accountId, roleId, roleName, permissions) => {
    setUnassignTarget({ accountId, roleId, roleName, permissions });
    setUnassignDialogOpen(true);
  };

  const closeUnassignDialog = () => {
    setUnassignDialogOpen(false);
    setUnassignTarget(null);
  };

  const handleUnassign = async () => {
    if (!unassignTarget) return;

    // Prevent removing own admin-granting role (checks permissions, not just name)
    if (
      unassignTarget.accountId === currentUser?.accountId &&
      roleGrantsAdminAccess(unassignTarget.permissions)
    ) {
      setError('Cannot remove your own admin role');
      closeUnassignDialog();
      return;
    }

    setUnassigning(true);

    try {
      await unassignRole(unassignTarget.accountId, unassignTarget.roleId);
      closeUnassignDialog();

      // Refresh relevant data
      if (selectedRole?.roleId === unassignTarget.roleId) {
        loadAccountsForRole(unassignTarget.roleId);
      }
      if (searchedAccountRoles?.accountId === unassignTarget.accountId) {
        const data = await getAccountRoles(unassignTarget.accountId);
        setSearchedAccountRoles(data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to unassign role');
      closeUnassignDialog();
    } finally {
      setUnassigning(false);
    }
  };

  return (
    <Stack spacing={3}>
      {/* Search by Account ID */}
      <Card
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Search User by Account ID
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter an account ID to view and manage their role assignments.
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Account ID"
              value={searchAccountId}
              onChange={(e) => setSearchAccountId(e.target.value)}
              fullWidth
              size="small"
              placeholder="Enter account UUID"
            />
            <Button variant="contained" onClick={handleSearchAccount} disabled={searchLoading}>
              {searchLoading ? <DWLFLoader size="tiny" showText={false} /> : 'Search'}
            </Button>
          </Stack>

          {searchError && <Alert severity="error" sx={{ mb: 2 }}>{searchError}</Alert>}

          {searchedAccountRoles?.roles && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon />
                  <Typography variant="subtitle1">
                    Account: {searchedAccountRoles.accountId}
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openAssignDialog(searchedAccountRoles.accountId)}
                >
                  Assign Role
                </Button>
              </Stack>

              {searchedAccountRoles.roles.length === 0 ? (
                <Alert severity="info">This account has no roles assigned.</Alert>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Role</TableCell>
                        <TableCell>Assigned At</TableCell>
                        <TableCell>Assigned By</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchedAccountRoles.roles.map((role) => (
                        <TableRow key={role.roleId}>
                          <TableCell>
                            <Chip label={role.name} size="small" />
                          </TableCell>
                          <TableCell>
                            {role.assignedAt ? new Date(role.assignedAt).toLocaleString() : '-'}
                          </TableCell>
                          <TableCell>{role.assignedBy || '-'}</TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                searchedAccountRoles.accountId === currentUser?.accountId &&
                                roleGrantsAdminAccess(role.permissions)
                                  ? 'Cannot remove your own admin role'
                                  : 'Remove role'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    openUnassignDialog(searchedAccountRoles.accountId, role.roleId, role.name, role.permissions)
                                  }
                                  disabled={
                                    searchedAccountRoles.accountId === currentUser?.accountId &&
                                    roleGrantsAdminAccess(role.permissions)
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Browse by Role */}
      <Card
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
        }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Browse Users by Role
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a role to see all accounts that have it assigned.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Assign role to user">
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openAssignDialog()}>
                  Assign Role
                </Button>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={loadRoles} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <DWLFLoader size="medium" showText={false} />
            </Box>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                {roles.map((role) => (
                  <Chip
                    key={role.roleId}
                    label={role.name}
                    onClick={() => setSelectedRole(role)}
                    variant={selectedRole?.roleId === role.roleId ? 'filled' : 'outlined'}
                    color={selectedRole?.roleId === role.roleId ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>

              {selectedRole && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Users with role: <strong>{selectedRole.name}</strong>
                  </Typography>

                  {accountError && <Alert severity="error" sx={{ mb: 2 }}>{accountError}</Alert>}

                  {loadingAccounts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <DWLFLoader size="medium" showText={false} />
                    </Box>
                  ) : accountsWithRole.length === 0 ? (
                    <Alert severity="info">No users have this role assigned.</Alert>
                  ) : (
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Account ID</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Assigned At</TableCell>
                            <TableCell>Assigned By</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {accountsWithRole.map((account) => (
                            <TableRow key={account.accountId}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {account.accountId}
                                </Typography>
                              </TableCell>
                              <TableCell>{account.email || '-'}</TableCell>
                              <TableCell>
                                {account.assignedAt ? new Date(account.assignedAt).toLocaleString() : '-'}
                              </TableCell>
                              <TableCell>{account.assignedBy || '-'}</TableCell>
                              <TableCell align="right">
                                <Tooltip
                                  title={
                                    account.accountId === currentUser?.accountId && roleGrantsAdminAccess(selectedRole.permissions)
                                      ? 'Cannot remove your own admin role'
                                      : 'Remove role'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        openUnassignDialog(account.accountId, selectedRole.roleId, selectedRole.name, selectedRole.permissions)
                                      }
                                      disabled={
                                        account.accountId === currentUser?.accountId && roleGrantsAdminAccess(selectedRole.permissions)
                                      }
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Assign Role Dialog */}
      <Dialog open={assignDialogOpen} onClose={closeAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Role to User</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {assignError && <Alert severity="error">{assignError}</Alert>}

            <TextField
              label="Account ID"
              value={assignAccountId}
              onChange={(e) => setAssignAccountId(e.target.value)}
              fullWidth
              required
              placeholder="Enter account UUID"
            />

            <Autocomplete
              options={roles}
              getOptionLabel={(option) => option.name}
              value={roles.find((r) => r.roleId === assignRoleId) || null}
              onChange={(_, newValue) => setAssignRoleId(newValue?.roleId || '')}
              renderInput={(params) => <TextField {...params} label="Role" required />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={assigning}>
            {assigning ? <DWLFLoader size="tiny" showText={false} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unassign Confirmation Dialog */}
      <Dialog open={unassignDialogOpen} onClose={closeUnassignDialog}>
        <DialogTitle>Remove Role Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove the "{unassignTarget?.roleName}" role from account{' '}
            {unassignTarget?.accountId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUnassignDialog}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleUnassign} disabled={unassigning}>
            {unassigning ? <DWLFLoader size="tiny" showText={false} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default UserRoleAssignment;
